import { useState } from "react";
import { Link } from "react-router";
import type { StudentSummary } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { agoLabel, nextLabel } from "../../lib/when.ts";
import { homeworkFact, type HomeworkFact } from "../../lib/student-facts.ts";
import { NothingFound } from "../../lib/SearchField.tsx";
import { Empty } from "../../lib/Empty.tsx";
import { CopyLink } from "../shell/CopyLink.tsx";

/**
 * Ученики и постоянные ссылки-приглашения.
 *
 * Приглашение принадлежит УЧЕНИКУ, а не уроку (решение владельца 09.08.2026,
 * раздел 5.10 CLAUDE.md): преподаватель заводит ученика, получает одну ссылку
 * и отдаёт её один раз. Дальше ученик ходит по ней на все занятия.
 *
 * Код показывается ровно один раз, в ответе на заведение: в базе лежит только
 * SHA-256. Поэтому «покажите ссылку ещё раз» это не кнопка «показать», а
 * кнопка «перевыпустить», и старая ссылка при этом отзывается. Иначе хеш в
 * базе не значил бы ничего.
 *
 * ⚠️ **Выпуск ссылки живёт здесь, отзыв и архив на карточке.** 18.08.2026 в
 * строке не осталось ни одного действия про доступ, и 20.08.2026 на это
 * пожаловались: чтобы отдать ученику ссылку заново, нужно было четыре нажатия
 * и прокрутка (строка, окно, «открыть целиком», вниз до «Доступа»). Разбор
 * показал, что прошлое решение свалило в одну кучу два разных действия:
 *
 *   ПЕРЕВЫПУСК  частый, обратимый (следующий перевыпуск чинит промах), и
 *               результат нужен человеку В РУКИ, прямо сейчас
 *   ОТЗЫВ       редкий, необратимый, объясняется пятью строками последствий
 *
 * Первое вернулось в строку, второе осталось экраном. Строка для кода даже
 * безопаснее карточки-окна: окно закрывается щелчком мимо и по Esc, а код
 * показывается ровно один раз, тогда как развёртка под строкой (`row__more`)
 * не закрывается ничем случайным. Ровно в ней код и показывается при заведении
 * ученика, то есть механизм не новый.
 *
 * Список строками, а не карточками (18.08.2026): владелец сказал «всё вроде
 * есть, но плывёт». Плавала подпись, собранная из того, что не пусто, поэтому
 * теперь у каждого ученика ровно пять слотов, и каждый занят всегда. Нет
 * данных, в слоте стоят слова про их отсутствие.
 *
 * ⚠️ **Заведения здесь больше НЕТ** (14.09.2026, подход 2е переноса
 * дизайн-системы). Форма из одного поля с кнопкой над списком читалась как
 * поиск, и настоящий поиск теперь занял её место; заведение стало главным
 * действием экрана и живёт окном в `StudentsPage`. Вместе с формой отсюда
 * уехала и карта показанных кодов: писали в неё двое, окно и кнопка «Новая
 * ссылка» в строке, и общий владелец у них экран.
 */
export function StudentsPanel(props: {
  /**
   * Все ученики, а не только отобранные поиском.
   *
   * ⚠️ Списка два, и оба обязательны. Рисуется `shown`, а полный список
   * отвечает на вопрос «пусто совсем или только по запросу»: у пустого
   * кабинета и у пустой выборки разные выходы, плашка заведения против
   * «снять запрос».
   *
   * ⚠️ Списка уроков здесь нет: он грузился ради четырёх занятий в строке, а
   * они и были главным источником плавания (у одного ученика четыре даты, у
   * соседнего ноль). Полный список занятий живёт на карточке ученика, и на
   * канале 128 кбит/с это минус целый круг по сети при каждом открытии экрана.
   */
  students: StudentSummary[];
  /** Что показывать: те же ученики после отбора поиском. */
  shown: readonly StudentSummary[];
  /** Что набрано в поиске. Нужно ровно затем, чтобы назвать запрос словами. */
  query: string;
  onClearQuery: () => void;
  /**
   * Открыть окно заведения. Тот же обработчик, что у кнопки в шапке экрана:
   * пустая плашка зовёт завести первого ученика, и звать она обязана туда же,
   * а не в свою копию формы.
   */
  onAdd: () => void;
  /** Коды, показанные в этой сессии экрана. Владелец карты это экран. */
  links: Record<string, string>;
  onLink: (userId: string, inviteUrl: string) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /**
   * У кого сейчас спрашивают подтверждение на новую ссылку.
   *
   * Один на весь список, а не флаг в каждой строке: спрашивать одновременно у
   * двоих незачем, а два открытых подтверждения на экране это приглашение
   * нажать не в том месте.
   */
  const [asking, setAsking] = useState<string | null>(null);

  /** Список пуст совсем, а не «ничего не нашлось»: у этих двух разные выходы. */
  const empty = props.students.length === 0;

  // Одно «сейчас» на всю отрисовку: с `Date.now()` в каждой строке соседние
  // ярлыки считались бы от разных миллисекунд, а на границе суток и от разных
  // дней
  const now = Date.now();

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      props.onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Выпустить ученику новую ссылку.
   *
   * Одна ручка на оба случая («ссылки нет» и «ссылка есть»): сервер в обоих
   * отвечает новым кодом и гасит прежний, если тот был. Разница только в том,
   * спрашиваем ли мы перед этим (см. `StudentRow`).
   */
  const issue = (userId: string) => {
    setAsking(null);
    void run(async () => {
      const { inviteUrl } = await api.reissueInvite(userId);
      props.onLink(userId, inviteUrl);
    });
  };

  return (
    <section className="card">
      {/* Заголовка у панели нет: его несёт шапка экрана. Раньше панель была
          половиной общей главной и называла себя сама */}
      {err && <p className="err">{err}</p>}

      {empty ? (
        /*
          Что здесь неочевидно новому преподавателю, так это свойства ссылки:
          она постоянная (одна на все занятия, а не на ближайшее) и показывается
          ровно один раз, потому что в базе от неё только SHA-256. Человек,
          закрывший вкладку, не потерявшись, а просто отвлёкшись, идёт
          перевыпускать. Поэтому оба свойства стоят ДО того, как нажата кнопка.

          ⚠️ Заголовок говорит про СПИСОК, а не про человека: «учеников пока
          нет», а не «вы ещё никого не завели» (раздел 5.10 CLAUDE.md).

          ⚠️ Кнопка плашки и кнопка шапки это одно действие и один обработчик.
          Разбор того, почему их всё-таки две, стоит у `EmptyAction` в
          `lib/Empty.tsx`.
        */
        <Empty
          icon="students"
          title="Учеников пока нет"
          text="Ссылка ученика постоянная: по ней он заходит на все занятия с вами. Показывается она один раз, поэтому отдайте её сразу."
          action={{ label: "Завести ученика", onClick: props.onAdd }}
        />
      ) : props.shown.length === 0 ? (
        <NothingFound
          query={props.query}
          nothing="ни одного ученика"
          onClear={props.onClearQuery}
        />
      ) : (
        <ul className="list list--students" data-rows="page">
          <li className="list__head" aria-hidden="true">
            <span>Ученик</span>
            <span>Следующий урок</span>
            <span>Домашка</span>
            <span>Ссылка</span>
            <span>Действия</span>
          </li>
          {/*
            Порядок оставлен серверный (пара, заведённая последней, сверху).
            Сортировать по тому, что горит, здесь нельзя: список перечитывается
            после каждого действия в строке, а строка это место, куда человек
            только что нажимал. Переставленный список заставил бы искать того же
            ученика заново.
          */}
          {props.shown.map((s) => (
            <StudentRow
              key={s.userId}
              student={s}
              now={now}
              freshLink={props.links[s.userId]}
              busy={busy}
              asking={asking === s.userId}
              onAsk={() => setAsking(s.userId)}
              onCancel={() => setAsking(null)}
              onIssue={() => issue(s.userId)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/** Строка одного ученика: пять слотов, каждый занят всегда. */
function StudentRow(props: {
  student: StudentSummary;
  now: number;
  /** Код, выданный в этой сессии экрана. Второй раз сервер его не покажет. */
  freshLink: string | undefined;
  busy: boolean;
  /** У этого ученика сейчас спрашивают подтверждение на новую ссылку. */
  asking: boolean;
  onAsk: () => void;
  onCancel: () => void;
  onIssue: () => void;
}) {
  const s = props.student;
  /*
    ⚠️ Карточка открывается ПЕРЕХОДОМ на страницу, а не окном поверх списка
    (решение владельца 13.09.2026). До 14.09.2026 тот же адрес рисовался двумя
    разными экранами: щелчок из списка давал окно, а прямой заход страницу, и
    расходилась у них не обвязка, а набор блоков. Остался один экран, и он
    страница: у детали есть свой адрес, а это единственное место в продукте, где
    раскрытие детали строки не аккордеон.
  */
  const next = nextLabel(s.nextLessonAt ?? null, props.now);
  const last = agoLabel(s.lastLessonAt ?? null, props.now, "занятий не было");
  const link = invitePill(s);
  /*
    Факт домашки считается ОДИН раз на строку, потому что читают его теперь
    двое: слот «Домашка» и подпись под именем. Две копии `homeworkFact` дали бы
    строку, где колонка говорит «просрочено 23 дня», а подпись молчит.
  */
  const hw = homeworkFact(s.lastHomework, props.now);

  return (
    <li className="row" data-dim={s.unlinked}>
      <span className="cell row__name">
        {/*
          Отдельный узел под именем остался от галочки выбора, которая стояла
          рядом с ним до 17.09.2026: их держали одной строкой, иначе на
          телефоне галочка уезжала от своего имени. Сегодня внутри один
          ребёнок, и узел ничего не раскладывает; снят он не был потому, что
          правка раскладки строки не ловится ни типами, ни тестами, а поводом
          была бы одна лишняя обёртка.
        */}
        <span className="card__name">
          {/* Имя это переход на карточку: там ссылка, архив и занятия */}
          <Link to={`/students/${s.userId}`}>{s.displayName}</Link>
        </span>
        {/*
          Просрочка стоит ПЕРВОЙ из двух подписей, и обе всегда в разметке.
          Выбирает между ними CSS: на широкой просрочка спрятана (у неё там своя
          колонка), на узкой она показана и гасит соседнюю подпись правилом
          `.row__sub--late + .row__sub`. Ветки по ширине в React нет намеренно,
          это был бы второй источник правды о раскладке.

          Просрочка это единственный факт строки, у которого нет второго адреса
          (Р3 в `docs/phone-design-decisions.md`): всё прочее спрятанное можно
          посмотреть на своём экране, а этот нельзя, поэтому он и занимает место
          подписи. Текст берётся у общего `homeworkFact`, своего тут нет ни
          слова.
        */}
        {hw.kind === "overdue" && (
          <span className="row__sub row__sub--late" title={hw.title ?? undefined}>
            {hw.when.text}
          </span>
        )}
        {/* Прошлое занятие второй строкой, а не колонкой: вопрос «когда мы
            виделись» задают реже, чем «когда встретимся», и колонку он не
            заслужил, а слот в подписи заслужил. Приставка стоит только при
            дате: у её отсутствия свой полный текст, и «последнее занятие:
            занятий не было» это не по-русски */}
        <span className="row__sub" title={last.title || undefined}>
          {(s.lastLessonAt ?? null) === null ? last.text : `последнее занятие: ${last.text}`}
        </span>
      </span>

      <span
        className="cell row__when"
        data-label="Следующий"
        data-narrow="keep"
        data-tone={next.tone}
        title={next.title || undefined}
      >
        {next.text}
      </span>

      <HomeworkCell fact={hw} />

      {/* Плашку получает состояние, а отсутствие ссылки говорится словами: у
          невыпущенной ссылки состояния нет, ей нечему меняться, пока её не
          выпустили */}
      <span className="cell" data-label="Ссылка">
        {link.look === "words" ? (
          <span className="row__void" title={link.title}>
            {link.text}
          </span>
        ) : (
          <span
            className="pill"
            data-tone={link.look === "warn" ? "warn" : undefined}
            title={link.title}
          >
            {link.text}
          </span>
        )}
      </span>

      <span className="cell row__act">
        {/*
          Подпись кнопки одна на оба состояния, и это не лень. «Выпустить» и
          «Перевыпустить» разной длины, а колонка узкая: строки с активной и
          неактивной ссылкой получили бы разную высоту, то есть ровно то
          плавание, ради которого список переделан в слоты. «Новая ссылка»
          верна в обоих случаях: сервер и там и там отвечает новым кодом.
        */}
        <button
          className="btn btn--sm btn--quiet"
          onClick={s.inviteActive ? props.onAsk : props.onIssue}
          disabled={props.busy}
          /* В списке из тридцати строк одинаковая подпись без имени это
             тридцать одинаковых кнопок для читалки */
          aria-label={`Новая ссылка для ученика ${s.displayName}`}
        >
          Новая ссылка
        </button>
        {/* Переход на карточку ссылкой, а не кнопкой: это адрес */}
        <Link className="btn btn--sm" to={`/students/${s.userId}`}>
          Карточка
        </Link>
      </span>

      {(props.asking || props.freshLink !== undefined) && (
        // Развёрткой на всю ширину, а не в колонке: код показывается ровно
        // один раз, в ответе на заведение, и в 7rem колонки он не влезает
        // никак. Пропасть ему здесь нельзя, второй раз сервер его не покажет
        <div className="row__more">
          {props.asking && (
            <>
              {/*
                Спрашиваем только при живой ссылке. Последствие ровно одно и
                названо до нажатия: та ссылка, что у ученика на руках, умрёт
                сейчас же. Про отзыв здесь ни слова, отзыв это другая кнопка на
                карточке, и путать их нельзя.
              */}
              <p className="muted">
                Ссылка, которая уже есть у ученика, перестанет работать сразу. Новую нужно будет
                отдать ему заново.
              </p>
              <div className="acts">
                {/*
                  Обычный вес, не акцент и не красный. Акцент на экране занят
                  главным действием шапки, «Завести ученика», а красный это
                  необратимое, то есть отзыв на карточке: перевыпуск чинится
                  следующим перевыпуском. Отмена рядом обязана быть тише
                  подтверждения.
                */}
                <button className="btn" onClick={props.onIssue} disabled={props.busy}>
                  Да, выпустить новую
                </button>
                <button className="btn btn--quiet" onClick={props.onCancel} disabled={props.busy}>
                  Отмена
                </button>
              </div>
            </>
          )}
          {props.freshLink !== undefined && <CopyLink url={props.freshLink} />}
        </div>
      )}
    </li>
  );
}

/**
 * Судьба ссылки одним слотом.
 *
 * Раньше это стояло в двух местах строки сразу: тег справа («по ссылке» или
 * «вход привязан») и целая фраза под ним про то, выдана ли ссылка. Два места
 * на один вопрос это и есть плавающая разметка, поэтому теперь слот один, а
 * подробности уехали в подсказку.
 *
 * ⚠️ Исходов три, а видов два. Плашку получает только СОСТОЯНИЕ, которое может
 * измениться: работающая ссылка и отозванная. У невыпущенной состояния нет,
 * значение пустое, и пустое говорится словами тоном `--muted`, как «занятий не
 * было» слотом левее.
 */
function invitePill(s: StudentSummary): {
  text: string;
  title: string;
  /** `warn` и `plain` это тона пилюли, `words` означает, что плашки нет вовсе. */
  look: "warn" | "plain" | "words";
} {
  if (s.unlinked) {
    return {
      text: "отвязан",
      look: "warn",
      title: "Ссылка больше не работает, новых занятий по паре не завести",
    };
  }
  // Признак «вход привязан» отдельной колонки не занимает: он меняется раз в
  // жизни ученика и нужен ровно тогда, когда разбираются с его доступом
  const who = s.claimed
    ? "Вход привязан: у ученика свой аккаунт"
    : "Входа нет: ученик ходит по ссылке";
  return s.inviteActive
    ? { text: "активна", look: "plain", title: `Ссылка выдана. ${who}` }
    : {
        text: "не выпущена",
        look: "words",
        title: `Ссылки нет, выпустить её можно на карточке. ${who}`,
      };
}

/**
 * Домашка: висит или нет.
 *
 * Состояние приходит вычисленным (`state`), просрочку считает общий предикат
 * `homeworkOverdue` из `@edu/shared`, тот же, которым отбирает сервер. Своего
 * сравнения дат здесь нет намеренно: два места однажды разошлись бы, и список
 * учеников начал бы спорить со списком работ.
 */
function HomeworkCell(props: { fact: HomeworkFact }) {
  /*
    Что случилось с работой, решает общий `homeworkFact`: тот же расчёт читает
    плитка «Домашка» в карточке ученика. Своя копия условий здесь дала бы список,
    где работа просрочена, и карточку того же ученика, где она «в работе».

    ⚠️ Считает его СТРОКА и отдаёт сюда готовым: с 15.09.2026 тот же факт читает
    подпись под именем, и второй расчёт разошёлся бы с первым.

    Вид при этом свой: здесь есть колонка, поэтому состояние стоит пилюлей, а
    срок отдельным моноширинным ярлыком рядом с ним.
  */
  const fact = props.fact;

  if (fact.kind === "none") {
    return (
      <span className="cell row__void" data-label="Домашка">
        {fact.label}
      </span>
    );
  }

  if (fact.kind === "overdue") {
    /*
      ⚠️ Плашки здесь нет, и это не потеря заметности. До 14.09.2026 просрочка
      была единственной сплошной заливкой кабинета и самым ярким пятном светлой
      темы: горела она в каждой второй строке, то есть выделяла не работу, а
      список целиком. Слово «просрочена» рядом с ярлыком тоже не стоит: срок и
      есть состояние, а «просрочена, просрочено 23 дня» это один факт дважды.
    */
    return (
      <span className="cell" data-label="Домашка">
        <span
          className="row__when"
          data-tone="danger"
          title={`${fact.title ?? ""}, срок ${fact.when.title}`}
        >
          {fact.when.text}
        </span>
      </span>
    );
  }

  return (
    <span className="cell" data-label="Домашка">
      <span
        className="pill"
        data-tone={fact.kind === "done" ? "ok" : undefined}
        title={fact.title ?? undefined}
      >
        {fact.label}
      </span>{" "}
      {/* Время рядом с состоянием, а не вместо него: «не открыта» со сроком
          через неделю и «не открыта» со сроком сегодня это разные новости.
          Тон берётся у ярлыка, поэтому сегодняшний срок подсвечен, а
          послезавтрашний нет */}
      <span className="row__when" data-tone={fact.when.tone} title={fact.when.title || undefined}>
        {fact.when.text}
      </span>
    </span>
  );
}
